const RESIZE_OBSERVER_LOOP_ERROR_MESSAGES = [
  'ResizeObserver loop completed with undelivered notifications.',
  'ResizeObserver loop limit exceeded',
] as const
const EXTERNAL_DOM_MUTATION_ERROR_MESSAGES = [
  // Browser translators and extensions can move/remove nodes outside React's
  // tree; React then reports this exact unmount-time removeChild TypeError.
  "Cannot read properties of null (reading 'removeChild')",
] as const
const NEXT_WEBPACK_RUNTIME_MODULE_CALL_ERROR_MESSAGE =
  "Cannot read properties of undefined (reading 'call')"
const VERCEL_SPEED_INSIGHTS_LOCAL_SCRIPT_ERROR_MESSAGE = "Unexpected token '<'"
const METAMASK_EXTENSION_NOT_FOUND_MESSAGE = 'MetaMask extension not found'
const URBAN_VPN_EXECUTOR_ERROR_MESSAGE = "Cannot read properties of undefined (reading 'M_ID')"
const URBAN_VPN_EXECUTOR_URL =
  /^(?:chrome-extension:\/\/eppiocemhmnlbhjplcgkofciiegomcon|app:\/\/)\/executors\/200\.js$/
const BROWSER_STACK_FRAME_LOCATION = /(?:^|\s|\()([a-z-]+:\/\/[^\s)]+):\d+:\d+\)?$/
const CHROME_SHUTDOWN_ABORT_ERROR_NAME = 'AbortError'
const CHROME_SHUTDOWN_ABORT_ERROR_MESSAGE = 'Failed due to shutdown'
const CHROME_SHUTDOWN_ABORT_ERROR_SIGNATURES = [
  CHROME_SHUTDOWN_ABORT_ERROR_MESSAGE,
  `${CHROME_SHUTDOWN_ABORT_ERROR_NAME}: ${CHROME_SHUTDOWN_ABORT_ERROR_MESSAGE}`,
  `Error: ${CHROME_SHUTDOWN_ABORT_ERROR_NAME}: ${CHROME_SHUTDOWN_ABORT_ERROR_MESSAGE}`,
] as const
const NON_ACTIONABLE_BROWSER_REJECTION_MESSAGES = [
  'Non-Error promise rejection captured with value: undefined',
  'Event `Event` (type=error) captured as promise rejection',
  'AbortError: signal is aborted without reason',
  'signal is aborted without reason',
  'AbortError: The play() request was interrupted because the media was removed from the document. https://goo.gl/LdLk22',
  'AbortError: The play() request was interrupted by a call to pause(). https://goo.gl/LdLk22',
  'NotSupportedError: Failed to load because no supported source was found.',
  'Error: NotSupportedError: Failed to load because no supported source was found.',
  'TypeError: Failed to fetch',
  'TypeError: Load failed',
  'TypeError: network error',
  'Error: fetch failed',
] as const
const LEAFLET_TRANSIENT_POSITION_ERROR_MESSAGE =
  "undefined is not an object (evaluating 't._leaflet_pos')"
const NON_ACTIONABLE_NETWORK_ERROR_MESSAGES = new Set([
  'Failed to fetch',
  'Load failed',
  'network error',
  'fetch failed',
])
const SUPABASE_AUTH_LOCK_ERROR_MESSAGES = [
  "AbortError: Lock broken by another request with the 'steal' option.",
  'AbortError: Lock was stolen by another request',
] as const
const AGENT_BACKEND_REQUEST_TIMED_OUT_MESSAGE = 'Backend request timed out'
const EXPECTED_AGENT_CLIENT_ERROR_MESSAGES = [
  'AgentRequestError: Billing required',
  'AgentRequestError: Not a workspace member',
  `AgentRequestError: ${AGENT_BACKEND_REQUEST_TIMED_OUT_MESSAGE}`,
  `Error: ${AGENT_BACKEND_REQUEST_TIMED_OUT_MESSAGE}`,
  'Error: Agent service unreachable. Check that the API is running.',
] as const
const API_ERROR_NAME = 'ApiError'
const EXPECTED_API_CLIENT_ERROR_MESSAGES = new Set([
  'Visit not found',
  'Cannot transition from COMPLETED to COMPLETED',
  'Cannot transition from IN_PROGRESS to IN_PROGRESS',
])
const STORAGE_SECURITY_ERROR_NAME = 'SecurityError'
const STORAGE_SECURITY_ERROR_MESSAGE = 'The operation is insecure.'
const STORAGE_DENIED_BREADCRUMB_PREFIXES = [
  'Unable to initialize theme preference.',
  'Unable to read theme preference; falling back to system.',
  'Unable to save theme preference.',
  '[safe-storage] Failed to read ',
  '[safe-storage] Failed to write ',
  '[safe-storage] Failed to remove ',
] as const
const IGNORED_BROWSER_ERROR_MESSAGES = [
  ...RESIZE_OBSERVER_LOOP_ERROR_MESSAGES,
  ...EXTERNAL_DOM_MUTATION_ERROR_MESSAGES,
  ...NON_ACTIONABLE_BROWSER_REJECTION_MESSAGES,
  ...SUPABASE_AUTH_LOCK_ERROR_MESSAGES,
  ...EXPECTED_AGENT_CLIENT_ERROR_MESSAGES,
] as const
const SUPABASE_AUTH_LOCK_STOLEN_PATTERN =
  /^(?:Error: )?Lock "lock:sb-[a-z0-9]+-auth-token" was released because another request stole it$/
const AGENT_REQUEST_ERROR_NAME = 'AgentRequestError'
const EXPECTED_AGENT_REQUEST_ERROR_MESSAGES = new Set([
  'Billing required',
  'Not a workspace member',
  AGENT_BACKEND_REQUEST_TIMED_OUT_MESSAGE,
])

declare global {
  interface Window {
    __trellisIgnoredBrowserErrorGuardInstalled?: boolean
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function readString(value: unknown, key: string): string | null {
  if (!isRecord(value)) {
    return null
  }
  const property = value[key]
  return typeof property === 'string' ? property : null
}

function readMessage(value: unknown): string | null {
  if (typeof value === 'string') {
    return value
  }
  if (value instanceof Error) {
    return value.message
  }
  return readString(value, 'message')
}

function readErrorName(value: unknown): string | null {
  if (value instanceof Error) {
    return value.name
  }
  return readString(value, 'name') ?? readString(value, 'type')
}

function readStack(value: unknown): string | null {
  if (value instanceof Error) {
    return value.stack ?? null
  }
  return readString(value, 'stack')
}

function readExceptionStack(properties: Record<string, unknown>): string | null {
  return (
    readString(properties, '$exception_stack_trace_raw') ??
    readString(properties, '$exception_stack_trace')
  )
}

function isNextWebpackRuntimeStack(stack: string | null): boolean {
  if (!stack) {
    return false
  }
  return stack.includes('/_next/static/chunks/webpack-')
}

function isVercelSpeedInsightsStack(stack: string | null): boolean {
  if (!stack) {
    return false
  }
  return (
    stack.includes('/_vercel/speed-insights/script.js') ||
    stack.includes('/_vercel/insights/script.js')
  )
}

function isMetaMaskInjectedStack(stack: string | null): boolean {
  if (!stack) {
    return false
  }
  return stack.includes('/scripts/inpage.js')
}

function isIgnoredNextWebpackRuntimeError(message: string | null, stack: string | null): boolean {
  return (
    message?.trim() === NEXT_WEBPACK_RUNTIME_MODULE_CALL_ERROR_MESSAGE &&
    isNextWebpackRuntimeStack(stack)
  )
}

export function isNextWebpackRuntimeModuleCallError(value: unknown): boolean {
  return isIgnoredNextWebpackRuntimeError(readMessage(value), readStack(value))
}

function isIgnoredVercelSpeedInsightsLocalScriptError(
  message: string | null,
  stack: string | null
): boolean {
  return (
    message?.trim() === VERCEL_SPEED_INSIGHTS_LOCAL_SCRIPT_ERROR_MESSAGE &&
    isVercelSpeedInsightsStack(stack)
  )
}

function isIgnoredInjectedExtensionError(message: string | null, stack: string | null): boolean {
  return message?.trim() === METAMASK_EXTENSION_NOT_FOUND_MESSAGE && isMetaMaskInjectedStack(stack)
}

function isUrbanVpnExecutorStack(message: string | null, stack: string | null): boolean {
  if (message?.trim() !== URBAN_VPN_EXECUTOR_ERROR_MESSAGE || !stack) {
    return false
  }
  const frames = stack
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
  if (frames[0] === message || frames[0] === `TypeError: ${message}`) {
    frames.shift()
  }
  // Match only this confirmed injected executor, retaining mixed application stacks.
  return (
    frames.length > 0 &&
    frames.every((frame) => {
      const location = frame.match(BROWSER_STACK_FRAME_LOCATION)?.[1]
      return location !== undefined && URBAN_VPN_EXECUTOR_URL.test(location)
    })
  )
}

function stacktraceHasOnlyUrbanVpnExecutorFrames(stacktrace: unknown): boolean {
  if (!(isRecord(stacktrace) && Array.isArray(stacktrace.frames))) {
    return false
  }
  return (
    stacktrace.frames.length > 0 &&
    stacktrace.frames.every((frame) => {
      const locations = [readString(frame, 'filename'), readString(frame, 'absPath')].filter(
        (location) => location !== null
      )
      return (
        locations.length > 0 && locations.every((location) => URBAN_VPN_EXECUTOR_URL.test(location))
      )
    })
  )
}

function framePointsAtNextWebpackRuntime(frame: unknown): boolean {
  if (!isRecord(frame)) {
    return false
  }
  return (
    isNextWebpackRuntimeStack(readString(frame, 'filename')) ||
    isNextWebpackRuntimeStack(readString(frame, 'absPath')) ||
    isNextWebpackRuntimeStack(readString(frame, 'module'))
  )
}

function framePointsAtVercelSpeedInsights(frame: unknown): boolean {
  if (!isRecord(frame)) {
    return false
  }
  return (
    isVercelSpeedInsightsStack(readString(frame, 'filename')) ||
    isVercelSpeedInsightsStack(readString(frame, 'absPath')) ||
    isVercelSpeedInsightsStack(readString(frame, 'module'))
  )
}

function framePointsAtMetaMaskInjectedScript(frame: unknown): boolean {
  if (!isRecord(frame)) {
    return false
  }
  return (
    isMetaMaskInjectedStack(readString(frame, 'filename')) ||
    isMetaMaskInjectedStack(readString(frame, 'absPath')) ||
    isMetaMaskInjectedStack(readString(frame, 'module'))
  )
}

function stacktracePointsAtNextWebpackRuntime(stacktrace: unknown): boolean {
  if (!isRecord(stacktrace)) {
    return false
  }
  const { frames } = stacktrace
  if (!Array.isArray(frames)) {
    return false
  }
  return frames.some(framePointsAtNextWebpackRuntime)
}

function stacktracePointsAtVercelSpeedInsights(stacktrace: unknown): boolean {
  if (!isRecord(stacktrace)) {
    return false
  }
  const { frames } = stacktrace
  if (!Array.isArray(frames)) {
    return false
  }
  return frames.some(framePointsAtVercelSpeedInsights)
}

function stacktracePointsAtMetaMaskInjectedScript(stacktrace: unknown): boolean {
  if (!isRecord(stacktrace)) {
    return false
  }
  const { frames } = stacktrace
  if (!Array.isArray(frames)) {
    return false
  }
  return frames.some(framePointsAtMetaMaskInjectedScript)
}

function isChromeShutdownAbortErrorMessage(message: string | null): boolean {
  const normalized = message?.trim()
  if (!normalized) {
    return false
  }
  return CHROME_SHUTDOWN_ABORT_ERROR_SIGNATURES.some((signature) => normalized === signature)
}

function isChromeShutdownAbortError(name: string | null, message: string | null): boolean {
  if (!isChromeShutdownAbortErrorMessage(message)) {
    return false
  }
  if (message?.trim() !== CHROME_SHUTDOWN_ABORT_ERROR_MESSAGE) {
    return true
  }
  return name === CHROME_SHUTDOWN_ABORT_ERROR_NAME
}

function isSupabaseAuthLockStolenMessage(name: string | null, message: string | null): boolean {
  const normalized = message?.trim()
  if (!normalized) {
    return false
  }
  return (
    SUPABASE_AUTH_LOCK_STOLEN_PATTERN.test(normalized) ||
    (name === 'AbortError' &&
      SUPABASE_AUTH_LOCK_ERROR_MESSAGES.some((ignored) => ignored === `AbortError: ${normalized}`))
  )
}

function isExpectedAgentClientError(name: string | null, message: string | null): boolean {
  const normalized = message?.trim()
  if (!normalized) {
    return false
  }
  if (normalized === 'Agent service unreachable. Check that the API is running.') {
    return true
  }
  if (normalized === 'Error: Agent service unreachable. Check that the API is running.') {
    return true
  }
  if (normalized === AGENT_BACKEND_REQUEST_TIMED_OUT_MESSAGE) {
    return name === 'Error' || name === AGENT_REQUEST_ERROR_NAME
  }
  if (normalized === `Error: ${AGENT_BACKEND_REQUEST_TIMED_OUT_MESSAGE}`) {
    return true
  }
  if (normalized.startsWith(`${AGENT_REQUEST_ERROR_NAME}: `)) {
    return EXPECTED_AGENT_REQUEST_ERROR_MESSAGES.has(
      normalized.slice(`${AGENT_REQUEST_ERROR_NAME}: `.length)
    )
  }
  return name === AGENT_REQUEST_ERROR_NAME && EXPECTED_AGENT_REQUEST_ERROR_MESSAGES.has(normalized)
}

function isExpectedApiClientError(name: string | null, message: string | null): boolean {
  const normalized = message?.trim()
  if (!normalized) {
    return false
  }
  if (normalized.startsWith(`${API_ERROR_NAME}: `)) {
    return EXPECTED_API_CLIENT_ERROR_MESSAGES.has(normalized.slice(`${API_ERROR_NAME}: `.length))
  }
  return name === API_ERROR_NAME && EXPECTED_API_CLIENT_ERROR_MESSAGES.has(normalized)
}

function isNonActionableNetworkError(name: string | null, message: string | null): boolean {
  const normalized = message?.trim()
  if (!normalized) {
    return false
  }
  return (
    (name === 'TypeError' || name === 'Error') &&
    (NON_ACTIONABLE_NETWORK_ERROR_MESSAGES.has(normalized) ||
      normalized.startsWith('Failed to fetch ('))
  )
}

function isLeafletTransientPositionError(message: string | null): boolean {
  return message?.trim() === LEAFLET_TRANSIENT_POSITION_ERROR_MESSAGE
}

function isStorageSecurityError(name: string | null, message: string | null): boolean {
  const normalized = message?.trim()
  if (!normalized) {
    return false
  }
  if (name === STORAGE_SECURITY_ERROR_NAME && normalized === STORAGE_SECURITY_ERROR_MESSAGE) {
    return true
  }
  return normalized === `${STORAGE_SECURITY_ERROR_NAME}: ${STORAGE_SECURITY_ERROR_MESSAGE}`
}

function isStorageDeniedBreadcrumbMessage(message: string | null): boolean {
  const normalized = message?.trim()
  if (!normalized) {
    return false
  }
  if (!STORAGE_DENIED_BREADCRUMB_PREFIXES.some((prefix) => normalized.startsWith(prefix))) {
    return false
  }
  return (
    normalized.includes('localStorage') ||
    normalized.includes(`${STORAGE_SECURITY_ERROR_NAME}: ${STORAGE_SECURITY_ERROR_MESSAGE}`)
  )
}

function breadcrumbPointsAtStorageDenied(breadcrumb: unknown): boolean {
  if (typeof breadcrumb === 'string') {
    return isStorageDeniedBreadcrumbMessage(breadcrumb)
  }
  if (!isRecord(breadcrumb)) {
    return false
  }
  return isStorageDeniedBreadcrumbMessage(readString(breadcrumb, 'message'))
}

function breadcrumbsPointAtStorageDenied(value: Record<string, unknown>): boolean {
  const { breadcrumbs } = value
  if (Array.isArray(breadcrumbs) && breadcrumbs.some(breadcrumbPointsAtStorageDenied)) {
    return true
  }
  if (isRecord(breadcrumbs) && Array.isArray(breadcrumbs.values)) {
    return breadcrumbs.values.some(breadcrumbPointsAtStorageDenied)
  }

  const { entries } = value
  if (!Array.isArray(entries)) {
    return false
  }
  return entries.some((entry) => {
    if (!isRecord(entry) || readString(entry, 'type') !== 'breadcrumbs') {
      return false
    }
    const { data } = entry
    if (!(isRecord(data) && Array.isArray(data.values))) {
      return false
    }
    return data.values.some(breadcrumbPointsAtStorageDenied)
  })
}

function isIgnoredErrorIdentity(errorName: string | null, message: string | null): boolean {
  return (
    isChromeShutdownAbortError(errorName, message) ||
    isSupabaseAuthLockStolenMessage(errorName, message) ||
    isExpectedAgentClientError(errorName, message) ||
    isExpectedApiClientError(errorName, message) ||
    isNonActionableNetworkError(errorName, message) ||
    isLeafletTransientPositionError(message) ||
    isIgnoredBrowserErrorMessage(message ?? '')
  )
}

function isIgnoredStackError(message: string | null, stack: string | null): boolean {
  return (
    isIgnoredNextWebpackRuntimeError(message, stack) ||
    isIgnoredVercelSpeedInsightsLocalScriptError(message, stack) ||
    isIgnoredInjectedExtensionError(message, stack) ||
    isUrbanVpnExecutorStack(message, stack)
  )
}

function stacktracePointsAtIgnoredError(message: string | null, stacktrace: unknown): boolean {
  return Boolean(
    (message?.trim() === NEXT_WEBPACK_RUNTIME_MODULE_CALL_ERROR_MESSAGE &&
      stacktracePointsAtNextWebpackRuntime(stacktrace)) ||
      (message?.trim() === VERCEL_SPEED_INSIGHTS_LOCAL_SCRIPT_ERROR_MESSAGE &&
        stacktracePointsAtVercelSpeedInsights(stacktrace)) ||
      (message?.trim() === METAMASK_EXTENSION_NOT_FOUND_MESSAGE &&
        stacktracePointsAtMetaMaskInjectedScript(stacktrace)) ||
      (message?.trim() === URBAN_VPN_EXECUTOR_ERROR_MESSAGE &&
        stacktraceHasOnlyUrbanVpnExecutorFrames(stacktrace))
  )
}

function isIgnoredPostHogMessage(properties: Record<string, unknown>): boolean {
  const message = readString(properties, '$exception_message')
  return Boolean(
    message &&
      (isIgnoredErrorIdentity(null, message) ||
        isIgnoredStackError(message, readExceptionStack(properties)))
  )
}

function isIgnoredSentryExceptionEvent(value: Record<string, unknown>): boolean {
  const { exception } = value
  if (!isRecord(exception)) {
    return false
  }
  const { values } = exception
  if (!Array.isArray(values)) {
    return false
  }

  return values.some((exceptionValue) => {
    if (!isRecord(exceptionValue)) {
      return false
    }
    const message = readString(exceptionValue, 'value') ?? readString(exceptionValue, 'message')
    const errorName = readErrorName(exceptionValue)
    if (isStorageSecurityError(errorName, message) && breadcrumbsPointAtStorageDenied(value)) {
      return true
    }
    if (isIgnoredErrorIdentity(errorName, message)) {
      return true
    }
    return stacktracePointsAtIgnoredError(message, exceptionValue.stacktrace)
  })
}

function isIgnoredPostHogExceptionList(properties: Record<string, unknown>): boolean {
  const { $exception_list: exceptionList } = properties
  if (!Array.isArray(exceptionList)) {
    return false
  }

  return exceptionList.some((exceptionValue) => {
    if (!isRecord(exceptionValue)) {
      return false
    }
    const message = readString(exceptionValue, 'value') ?? readString(exceptionValue, 'message')
    const errorName = readErrorName(exceptionValue)
    if (isIgnoredErrorIdentity(errorName, message)) {
      return true
    }
    return isIgnoredStackError(message, readExceptionStack(properties))
  })
}

export function getIgnoredBrowserErrorMessages(): string[] {
  return [...IGNORED_BROWSER_ERROR_MESSAGES]
}

export function isIgnoredBrowserErrorMessage(message: string): boolean {
  const normalized = message.trim()
  return IGNORED_BROWSER_ERROR_MESSAGES.some((ignoredMessage) => normalized === ignoredMessage)
}

export function isIgnoredBrowserError(value: unknown): boolean {
  if (typeof value === 'string' || value instanceof Error) {
    const message = readMessage(value)
    return (
      isIgnoredErrorIdentity(readErrorName(value), message) ||
      isIgnoredStackError(message, readStack(value))
    )
  }

  if (!isRecord(value)) {
    return false
  }
  if (isIgnoredSentryExceptionEvent(value)) {
    return true
  }
  const eventName = readString(value, 'event')
  if (eventName) {
    if (eventName !== '$exception') {
      return false
    }

    const { properties } = value
    if (!isRecord(properties)) {
      return false
    }

    if (isIgnoredPostHogExceptionList(properties)) {
      return true
    }

    return isIgnoredPostHogMessage(properties)
  }

  const errorName = readErrorName(value)
  const message = readString(value, 'message')
  if (isIgnoredErrorIdentity(errorName, message)) {
    return true
  }
  if (isStorageSecurityError(errorName, message) && breadcrumbsPointAtStorageDenied(value)) {
    return true
  }
  if (isIgnoredStackError(message, readStack(value))) {
    return true
  }

  const { properties } = value
  if (!isRecord(properties)) {
    return false
  }

  if (isIgnoredPostHogExceptionList(properties)) {
    return true
  }

  return isIgnoredPostHogMessage(properties)
}

export function installIgnoredBrowserErrorGuard(): void {
  if (typeof window === 'undefined') {
    return
  }
  if (window.__trellisIgnoredBrowserErrorGuardInstalled) {
    return
  }

  window.__trellisIgnoredBrowserErrorGuardInstalled = true
  window.addEventListener(
    'error',
    (event) => {
      if (!(isIgnoredBrowserError(event.message) || isIgnoredBrowserError(event.error))) {
        return
      }
      event.preventDefault()
      event.stopImmediatePropagation()
    },
    true
  )
  window.addEventListener(
    'unhandledrejection',
    (event) => {
      if (!isIgnoredBrowserError(event.reason)) {
        return
      }
      event.preventDefault()
      event.stopImmediatePropagation()
    },
    true
  )
}
